<script lang="ts">
  // Pre-duel deck picker. Shown right before entering ANY duel (World queue / NPC / invite, Free Duel
  // queue / lobby host / join) so the player chooses which saved deck to bring — there's no persistent
  // "equipped" deck. Loads the player's decks for the given format and hands the chosen NAME back via
  // onpick(); duel-entry messages carry that name to the server. Self-contained: owns its own DeckList
  // request + Decks subscription (the socket fans out to every listener).
  //
  // Inner controls are cyan (Tailwind can't compile interpolated colour classes, and Free Duel's own
  // UI already pairs a magenta panel edge with cyan buttons); `accent` only tints the DataWindow edge.
  import { fade, scale, fly } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { onMount } from "svelte";
  import { send, listen } from "../stores/net.svelte.ts";
  import type { DeckSummary, DuelFormat } from "@ygoevo/world-protocol";
  import DataWindow from "./ui/DataWindow.svelte";
  import { draggableDialog } from "../actions/draggableDialog.ts";

  let {
    format,
    title = "Choose Your Deck",
    subtitle = "",
    accent = "cyan",
    onpick,
    oncancel,
    onbuild,
  }: {
    format: DuelFormat;
    title?: string;
    subtitle?: string;
    accent?: "cyan" | "magenta";
    onpick: (deckName: string) => void;
    oncancel: () => void;
    onbuild: () => void;
  } = $props();

  const reduced =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Tournament main-deck bounds (mirrors the server's deck.ts + the Deck Editor). A deck the server
  // would refuse is shown disabled with the reason, never silently hidden.
  const MAIN_MIN = 40;
  const MAIN_MAX = 60;

  let decks = $state<DeckSummary[] | null>(null); // null = still loading
  listen("Decks", (m) => {
    if ((m.format ?? "world") !== format) return; // only this picker's mode
    decks = m.decks;
  });
  onMount(() => send({ t: "DeckList", format }));

  const legalOf = (d: DeckSummary) => d.main.length >= MAIN_MIN && d.main.length <= MAIN_MAX;
</script>

<div class="mobile-dialog-host fixed inset-0 z-50 grid place-items-center px-4" transition:fade={{ duration: reduced ? 0 : 160 }}>
  <button class="modal-scrim absolute inset-0 bg-void/50" onclick={oncancel} aria-label="Cancel deck selection"></button>
  <div
    class="mobile-full-dialog relative w-[min(440px,94vw)]"
    role="dialog"
    aria-modal="true"
    aria-label={title}
    use:draggableDialog
    transition:scale={reduced ? { duration: 0 } : { start: 0.96, opacity: 0, duration: 220, easing: expoOut }}
  >
    <DataWindow c={16} {accent} fill class="mobile-dialog-panel">
      <!-- header -->
      <div data-dialog-drag-handle class="dialog-drag-handle flex items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-3">
        <div class="min-w-0">
          <div class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-cyan">{title}</div>
          {#if subtitle}<div class="truncate text-[0.7rem] text-ink-dim">{subtitle}</div>{/if}
        </div>
        <button onclick={oncancel} class="shrink-0 font-display text-[0.66rem] uppercase tracking-[0.18em] text-ink-dim transition-colors hover:text-cyan">✕ Cancel</button>
      </div>

      <div class="mobile-dialog-scroll max-h-[58dvh] overflow-y-auto scroll-cyber p-2.5">
        {#if decks === null}
          <p class="grid place-items-center py-12 font-display text-sm text-ink-dim">Loading decks…</p>
        {:else if decks.length === 0}
          <div class="flex flex-col items-center gap-3 px-6 py-10 text-center">
            <div class="font-display text-sm font-semibold text-ink-dim">No decks yet</div>
            <div class="max-w-[28ch] text-xs text-ink-dim/60">Build a {MAIN_MIN}-{MAIN_MAX} card deck in the Deck Editor, then come back to duel.</div>
            <button
              onclick={onbuild}
              class="chamfer-btn bg-cyan/12 px-5 py-2 font-display text-[0.7rem] font-bold uppercase tracking-[0.12em] text-cyan transition-colors hover:bg-cyan/22"
              style="--c:6px"
            >
              Build a Deck
            </button>
          </div>
        {:else}
          <div class="space-y-1.5">
            {#each decks as d, i (d.name)}
              {@const legal = legalOf(d)}
              <button
                onclick={() => legal && onpick(d.name)}
                disabled={!legal}
                in:fly={reduced ? { duration: 0 } : { y: 8, duration: 220, delay: Math.min(i * 32, 200), easing: expoOut }}
                class="group flex w-full items-center gap-3 chamfer bg-void-2/45 px-3.5 py-2.5 text-left transition-shadow duration-150 {legal
                  ? 'hover:ring-1 hover:ring-cyan hover:glow-cyan'
                  : 'cursor-not-allowed opacity-60'}"
                style="--c:9px"
                title={legal ? `Duel with ${d.name}` : `Needs ${MAIN_MIN}-${MAIN_MAX} Main cards (has ${d.main.length})`}
              >
                <span class="grid size-9 shrink-0 place-items-center chamfer-btn {legal ? 'bg-cyan/12 text-cyan' : 'bg-void-2/60 text-ink-dim/50'} font-display text-sm font-bold tabular-nums" style="--c:6px">
                  {d.main.length}
                </span>
                <div class="min-w-0 flex-1">
                  <div class="truncate font-display text-sm font-bold text-ink">{d.name}</div>
                  <div class="flex items-center gap-2 text-[0.7rem] text-ink-dim">
                    <span class="tabular-nums">Main {d.main.length}</span>
                    {#if d.extra?.length}<span class="text-ink-dim/40">·</span><span class="tabular-nums">Extra {d.extra.length}</span>{/if}
                    {#if d.side?.length}<span class="text-ink-dim/40">·</span><span class="tabular-nums">Side {d.side.length}</span>{/if}
                    {#if !legal}<span class="text-ink-dim/40">·</span><span class="font-semibold uppercase tracking-wide text-magenta">{MAIN_MIN}-{MAIN_MAX} needed</span>{/if}
                  </div>
                </div>
                {#if legal}
                  <span class="shrink-0 font-display text-[0.66rem] font-bold uppercase tracking-[0.14em] text-cyan opacity-0 transition-opacity group-hover:opacity-100">Duel →</span>
                {/if}
              </button>
            {/each}
          </div>
        {/if}
      </div>
    </DataWindow>
  </div>
</div>
